import { useTranslations } from '@vocab/react';
import { Bleed, Box, IconChevron, Tag, Text } from 'braid-design-system';
import type { ReactElement, FC } from 'react';
import translations from './.vocab';
import * as styles from './styles.css';

interface FilterAccordionProps {
  label: string;
  selectedItems: string[];
  expanded: boolean;
  onClick: () => void;
  children: ReactElement;
}

export const FilterAccordion: FC<FilterAccordionProps> = ({
  label,
  expanded,
  onClick,
  children,
  selectedItems,
}) => {
  const { t } = useTranslations(translations);

  const maxSelectedItemsDisplay = 2;

  const remainingSelectedItemCount =
    selectedItems.length - maxSelectedItemsDisplay;

  const accordionButtonId = `${label}-accordion-button`;
  const accordionContentId = `${label}-accordion-region`;
  return (
    <Box className={styles.wrapper}>
      <Box className={styles.buttonAndSelectedItemsStack}>
        <Box
          id={accordionButtonId}
          label={label}
          component="button"
          className={styles.accordionButton}
          aria-controls={accordionContentId}
          aria-expanded={expanded}
          cursor="pointer"
          onClick={() => {
            onClick();
          }}
          tabIndex={0}
          padding="medium"
          paddingBottom={expanded ? 'small' : 'medium'}
        >
          <Text weight="strong" align="left">
            {label}
          </Text>
          <Text size="small">
            <IconChevron direction={expanded ? 'up' : 'down'} />
          </Text>
        </Box>
        {selectedItems.length > 0 && !expanded ? (
          <Bleed top="small">
            <Box component="ul" className={styles.selectedList}>
              {selectedItems
                .slice(0, maxSelectedItemsDisplay)
                .map((item, index) => (
                  <Box
                    key={`mobile-accordion-${label}-${index}`}
                    component="li"
                    aria-label={item}
                  >
                    <Tag size="small">{item}</Tag>
                  </Box>
                ))}
              {remainingSelectedItemCount > 0 ? (
                <Text component="li">
                  {t('remainingCount+ more', {
                    remainingCount: `${
                      remainingSelectedItemCount > 999
                        ? '999+'
                        : remainingSelectedItemCount
                    }`,
                  })}
                </Text>
              ) : null}
            </Box>
          </Bleed>
        ) : null}
      </Box>
      <Box
        id={accordionContentId}
        display={expanded ? undefined : 'none'}
        role="region"
        aria-labelledby={accordionButtonId}
        padding={expanded ? 'small' : 'none'}
        paddingTop={expanded ? 'xxsmall' : 'none'}
        overflow="hidden"
      >
        {children}
      </Box>
    </Box>
  );
};
